import { visibleProjects } from '@/demo/scoping'
import { engagementPhaseFor } from '@/modules/architecture'
import { gateStatusLabel, gateStatusIsClientFacing } from '@/demo/content/myBoard'
import {
  BENCH_IDS,
  GROUP_HEADCOUNT,
  MATCH_CANDIDATES,
  MATCH_POOL_SIZE,
  OFFICES,
  OPEN_TASK,
  OVERLOADED_IDS,
  PHASES,
  PORTFOLIO,
  WORK_PACKAGES,
  assignmentsForProject,
  personById,
  projectById,
  type Health,
  type Person,
  type Project,
  type RoleKey,
  type WorkPackage,
} from '@/demo/data'

/** Content for `/my/team` — the manager landing. */

export const MANAGER_CHROME = { moduleLabel: 'MY WORK', breadcrumb: 'My team' } as const

export const MANAGER_HEADER = {
  title: 'My team',
  lede: 'Where every engagement stands, who is over-loaded and who is free, and the whole team’s board in one place.',
} as const

export const MANAGER_LABELS = {
  progressHeading: 'Engagement progress',
  unscopedLink: 'All projects',
  unscopedRoute: '/projects',
  peopleAriaLabel: 'People insight',
  overloaded: 'Over 90% loaded',
  bench: 'On bench',
  gap: 'Capacity gap',
  teamBoard: 'Team board',
  teamBoardNote: 'Every work package on the engagements in scope — not only my own',
  needsPerson: 'Needs a person',
  columns: {
    engagement: 'Engagement',
    deliveryPhase: 'Delivery phase',
    engagementPhase: 'Engagement phase',
    gate: 'Gate',
    gateStatus: 'Gate status',
    health: 'Health',
    staffed: 'Staffed',
  },
} as const

export const MANAGER_DENSITY_NOTE =
  'Minimalist: the progress table carries one row per engagement and no burn-down; the people panels name people rather than plotting them. Budget, margin and utilisation figures stay on the utilisation surface, one click away. The capacity gap is a blocking fact — it is never withheld, and it stays on the landing until the task is staffed.'

/**
 * The manager landing shows a SCOPED subset of the portfolio, so it says so in
 * words and gives the route to the unscoped one (AC-3.4). Every figure is read.
 */
export const scopeLine = (role: RoleKey, officeCode: string) => {
  const scoped = visibleProjects(role)
  const office = OFFICES.find((o) => o.code === officeCode)
  const packages = WORK_PACKAGES.filter((w) => scoped.some((p) => p.id === w.projectId))
  return {
    officeName: office?.name ?? officeCode,
    officeCode,
    scopedCount: scoped.length,
    activeCount: PORTFOLIO.active,
    packageCount: packages.length,
    poolSize: GROUP_HEADCOUNT,
    officeCount: OFFICES.length,
  }
}

export interface ProgressRow {
  project: Project
  deliveryPhaseName: string
  engagementLabel: string
  gateName: string
  gateStatus: string
  gateIsClientFacing: boolean
  health: Health
  staffed: number
}

export const progressRows = (role: RoleKey): readonly ProgressRow[] =>
  visibleProjects(role).map((project) => {
    const engagement = engagementPhaseFor(project.phase)
    return {
      project,
      deliveryPhaseName: PHASES.find((p) => p.key === project.phase)?.name ?? project.phase,
      engagementLabel: `${engagement.number} · ${engagement.name}`,
      gateName: engagement.gateName,
      gateStatus: gateStatusLabel(project),
      gateIsClientFacing: gateStatusIsClientFacing(project),
      health: project.health,
      staffed: assignmentsForProject(project.id).length,
    }
  })

export interface LoadedPerson {
  person: Person
  figure: string
}

export const overloadedPeople = (): readonly LoadedPerson[] =>
  OVERLOADED_IDS.map((id) => {
    const person = personById(id)
    return { person, figure: `${person.loadPct}%` }
  })

export const benchPeople = (): readonly LoadedPerson[] =>
  BENCH_IDS.map((id) => {
    const person = personById(id)
    return { person, figure: `${person.freePct}% free` }
  })

export const overloadedNote = `Three of ${GROUP_HEADCOUNT}. Each is carrying a single engagement at near-full load — the risk is absence, not overtime.`

export const benchNote = `${personById(BENCH_IDS[1]).name} is a cohort-4 trainee from the Kosovo Competence Center; ${personById(BENCH_IDS[0]).name} came off an engagement in June.`

/**
 * The unstaffed capacity gap. It is the SAME fact as the allocation surface's
 * open task and as the board's `wp-vd-05` — deliberately, so the three screens
 * agree rather than contradicting each other.
 */
export const capacityGap = () => {
  const unstaffed = WORK_PACKAGES.find((w) => w.assigneeId === null)
  const best = MATCH_CANDIDATES.find((c) => c.tag === 'Best fit')
  const bestPerson = best?.personId ? personById(best.personId) : null
  return {
    workPackage: unstaffed ?? null,
    project: unstaffed ? projectById(unstaffed.projectId) : null,
    detail: `${OPEN_TASK.hours} h · starts in ${OPEN_TASK.startsInDays} days · ${OPEN_TASK.skills}. Unstaffed on the board.`,
    bestPerson,
    bestNote: bestPerson
      ? `Best fit from the pool of ${MATCH_POOL_SIZE}: ${bestPerson.name} at ${best?.fitPct}% fit, ${best?.freePct}% free.`
      : '',
  }
}

export const teamWorkPackages = (role: RoleKey): readonly WorkPackage[] => {
  const scoped = new Set(visibleProjects(role).map((p) => p.id))
  return WORK_PACKAGES.filter((w) => scoped.has(w.projectId))
}
